import React, { useState, useEffect } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import MUIModal from '../../common/MUIModal'
import ReasonDropdown from '../../common/ReasonDropdown'
import { apiConstants, postGeneric, postMultipart, getGeneric } from '../../common/constants'
import ExtraDocsEditor, { collectExtraDocs, categoryLabel, RTO_DOC_CATEGORIES } from './ExtraDocsEditor'

/**
 * Order statuses that already represent (or follow) the RTO flow. Marking is
 * blocked for these. Mirrors {@code ProshipConstants.RTO_STATUS_LIST} on the
 * backend — keep in sync if the backend list changes.
 */
export const RTO_SERIES_STATUSES = Object.freeze([
  'RTO_REQUESTED',
  'RTO',
  'RTO_INTRANSIT',
  'RTO_OUT_FOR_DELIVERY',
  'RTO_DELIVERED',
  'RTO_FAILED',
  'RTO_CONTACT_CUSTOMER_CARE',
  'RTO_SHIPMENT_DELAY',
])

/**
 * Statuses where marking RTO doesn't make sense (already terminal or pre-pickup).
 * Combined with {@link RTO_SERIES_STATUSES} for the CTA disable check.
 */
export const RTO_BLOCKED_STATUSES = Object.freeze([
  ...RTO_SERIES_STATUSES,
  'CANCELLED_ORDER',
  'DELIVERED',
])

/** True if the given status should disable the "Mark as RTO" CTA. */
export const isRtoBlocked = (status) =>
  status != null && RTO_BLOCKED_STATUSES.includes(status)

/**
 * Modal for marking a single shipment as RTO_REQUESTED. Designed to be reused
 * from the order detail page or any list-row action.
 *
 * @param {boolean}   open           Controlled visibility.
 * @param {function}  onClose        Called when the user dismisses or after a
 *                                   successful submit.
 * @param {string}    awbNumber      Pre-filled (read-only) AWB.
 * @param {function=} onSuccess      Optional callback fired after a successful
 *                                   mark — parent can refresh order detail.
 */
export default function MarkRtoRequestedModal({ open, onClose, awbNumber, additionalDocuments, invoiceValue, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar()
  const [reasonCode, setReasonCode] = useState('')
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [extraDocRows, setExtraDocRows] = useState([])

  // Already-attached extra docs are read-only here — this flow only ADDS new ones.
  const existingDocs = additionalDocuments?.extraDocs || []

  // Invoice-value threshold (from system_defaults) above which an RTO E-Way Bill is mandatory.
  const [ewayThreshold, setEwayThreshold] = useState(50000)
  useEffect(() => {
    if (!open) return
    let active = true
    getGeneric(apiConstants.RTO_CONFIG)
      .then((res) => {
        const v =
          res?.data?.rtoEwayBillMandatoryInvoiceValue ??
          res?.data?.responseObj?.rtoEwayBillMandatoryInvoiceValue
        if (active && v != null && !Number.isNaN(Number(v))) setEwayThreshold(Number(v))
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [open])

  const ewayBillMandatory =
    invoiceValue != null &&
    !Number.isNaN(Number(invoiceValue)) &&
    Number(invoiceValue) > ewayThreshold
  const hasRtoEwayBill =
    existingDocs.some((d) => d?.docType === 'RTO_EWAY_BILL') ||
    extraDocRows.some((r) => r?.docType === 'RTO_EWAY_BILL')

  const resetState = () => {
    setReasonCode('')
    setRemarks('')
    setErrors({})
    setSubmitting(false)
    setExtraDocRows([])
  }

  const fetchExtraDocUrl = async (doc) => {
    try {
      const res = await getGeneric(apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL, { awbNumber, docId: doc?.docId })
      const url = res?.data?.responseObj?.url || res?.data?.url
      if (url) window.open(url, '_blank')
      else enqueueSnackbar('Document URL missing', { variant: 'warning' })
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to fetch document', { variant: 'error' })
    }
  }

  const handleClose = () => {
    if (submitting) return
    resetState()
    onClose?.()
  }

  const validate = () => {
    const e = {}
    if (!awbNumber) e.awb = 'AWB is required'
    if (!reasonCode) e.reasonCode = 'Reason is required'
    if (!remarks || remarks.trim().length < 10) {
      e.remarks = 'Remarks are required (minimum 10 characters)'
    }
    if (remarks && remarks.length > 1000) {
      e.remarks = 'Remarks max 1000 characters'
    }
    if (ewayBillMandatory && !hasRtoEwayBill) {
      e.docs = `An RTO E-Way Bill is mandatory for invoice value above ₹${ewayThreshold.toLocaleString('en-IN')}.`
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async () => {
    if (!validate()) return
    // Validate + collect any new documents up-front (before the RTO call).
    const fd = new FormData()
    const { extraDocs: newDocs, error: docErr } = collectExtraDocs(extraDocRows, fd)
    if (docErr) {
      enqueueSnackbar(docErr, { variant: 'error' })
      return
    }
    setSubmitting(true)
    try {
      // Upload documents FIRST so the (possibly mandatory) RTO E-Way Bill is attached to the
      // order before the server-side mark check runs. The backend re-enforces the threshold rule.
      if (newDocs.length > 0) {
        try {
          const docPayload = { awbNumber, extraDocs: [...existingDocs, ...newDocs] }
          fd.append('data', new Blob([JSON.stringify(docPayload)], { type: 'application/json' }))
          await postMultipart(apiConstants.ADDITIONAL_DOCUMENTS_UPLOAD, fd)
        } catch (e) {
          enqueueSnackbar(
            'Document upload failed: ' + (e?.response?.data?.message || 'please retry'),
            { variant: 'error' },
          )
          setSubmitting(false)
          return
        }
      }
      const payload = {
        awbNumber: awbNumber,
        rtoReasonCode: reasonCode,
        remarks: remarks.trim(),
      }
      const res = await postGeneric(apiConstants.MARK_RTO_REQUESTED, payload)
      const status = res?.data?.status
      if (status !== 'SUCCESS') {
        const msg = res?.data?.errorMsg || res?.data?.responseObj || 'Failed to mark RTO'
        enqueueSnackbar(msg, { variant: 'error' })
        setSubmitting(false)
        return
      }
      enqueueSnackbar('Shipment marked as RTO Requested', { variant: 'success' })
      resetState()
      onSuccess?.()
      onClose?.()
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.errorMsg ||
          err?.response?.data?.message ||
          'Failed to mark RTO',
        { variant: 'error' },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <MUIModal
      open={open}
      handleClose={handleClose}
      title='Mark Shipment as RTO Requested'
      maxWidth='md'
      hideClose
      action={
        <Stack direction='row' spacing={1}>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={submit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : null}
          >
            {submitting ? 'Submitting…' : 'Mark RTO'}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity='warning'>
          <AlertTitle>Heads up</AlertTitle>
          The reason and remarks will be communicated to the courier partner.
          Do not include customer phone numbers, emails, or other personal
          information in the remarks.
        </Alert>

        <TextField
          label='AWB'
          size='small'
          value={awbNumber || ''}
          InputProps={{ readOnly: true }}
          fullWidth
          error={Boolean(errors.awb)}
          helperText={errors.awb || ' '}
        />

        <ReasonDropdown
          category='RTO_REQUEST'
          value={reasonCode}
          onChange={setReasonCode}
          label='RTO Reason'
          required
          error={errors.reasonCode}
        />

        <TextField
          label='Remarks'
          size='small'
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          required
          multiline
          minRows={3}
          maxRows={6}
          inputProps={{ maxLength: 1000 }}
          error={Boolean(errors.remarks)}
          helperText={
            errors.remarks ||
            `${remarks.length}/1000 characters (minimum 10 required)`
          }
          fullWidth
        />

        <Divider />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            Documents{ewayBillMandatory ? '' : ' (optional)'}
          </Typography>
          {ewayBillMandatory && (
            <Alert severity={hasRtoEwayBill ? 'success' : 'info'} sx={{ mb: 1 }}>
              Invoice value exceeds ₹{ewayThreshold.toLocaleString('en-IN')} — an{' '}
              <strong>RTO E-Way Bill</strong> document is required.
            </Alert>
          )}
          {errors.docs && (
            <Typography variant='caption' color='error' sx={{ display: 'block', mb: 1 }}>
              {errors.docs}
            </Typography>
          )}
          {existingDocs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                Already attached
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {existingDocs.map((xd) => (
                  <Link
                    key={xd.docId}
                    component='button'
                    type='button'
                    variant='caption'
                    onClick={() => fetchExtraDocUrl(xd)}
                  >
                    {xd.docLabel || categoryLabel(xd.docType)}
                  </Link>
                ))}
              </Box>
            </Box>
          )}
          <ExtraDocsEditor
            rows={extraDocRows}
            setRows={setExtraDocRows}
            onView={fetchExtraDocUrl}
            existingCount={existingDocs.length}
            categories={RTO_DOC_CATEGORIES}
          />
        </Box>

        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          AWB: <strong>{awbNumber}</strong>
        </Box>
      </Stack>
    </MUIModal>
  )
}
